import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import { u2000BuildAppRouteHref } from '../../../u2000Core/tools/u2000OpenAppRouteInNewTab';
import U2000CommonTableFooter from '../../../u2000Core/components/tables/basic/U2000CommonTableFooter';
import { NS } from '../../U3146';
import {
  u3146CloseLineIsClosed,
  type U3146CloseLineResult,
} from '../../U3146Service';

export type U3146CloseInactiveWizardCloseResultsProps = {
  results: U3146CloseLineResult[];
  onBackToList: () => void;
  onNewSession: () => void;
};

function resultStatusLabel(status: U3146CloseLineResult['status'], t: (key: string, opts?: Record<string, unknown>) => string): string {
  switch (status) {
    case 'closed':
      return t('wizardResultClosed', { ns: NS });
    case 'error':
      return t('wizardResultError', { ns: NS });
    default:
      return status;
  }
}

const U3146CloseInactiveWizardCloseResults: React.FC<U3146CloseInactiveWizardCloseResultsProps> = ({
  results,
  onBackToList,
  onNewSession,
}) => {
  const { t } = useTranslation();

  const { closedCount, errorCount } = useMemo(() => {
    let closed = 0;
    let errors = 0;
    for (const row of results) {
      if (u3146CloseLineIsClosed(row)) closed += 1;
      else if (row.status === 'error') errors += 1;
    }
    return { closedCount: closed, errorCount: errors };
  }, [results]);

  const summaryTone =
    errorCount === 0 ? 'border-green-200 bg-green-50 text-green-950' : 'border-amber-200 bg-amber-50 text-amber-950';

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900">{t('wizardResultsTitle', { ns: NS })}</h2>
        <p
          role="status"
          aria-live="polite"
          className={`mt-2 rounded-lg border p-3 text-sm ${summaryTone}`}
        >
          {t('wizardResultsSummary', {
            ns: NS,
            closed: closedCount,
            errors: errorCount,
            total: results.length,
          })}
        </p>
      </div>

      <div className="w-full min-w-0 overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblProjectNumber', { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('wizardColResult', { ns: NS })}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-700">{t('lblTecLinesWrittenOff', { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblWriteOffInvoice', { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('lblStandardInvoicesClosed', { ns: NS })}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('wizardColMessage', { ns: NS })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {results.map((row, idx) => {
              const closed = u3146CloseLineIsClosed(row);
              return (
                <tr key={`${row.projetNumber ?? row.projectId ?? idx}`} className={row.status === 'error' ? 'bg-red-50/50' : undefined}>
                  <td className="whitespace-nowrap px-3 py-2 font-medium">{row.projetNumber ?? '—'}</td>
                  <td className="px-3 py-2">{resultStatusLabel(row.status, t)}</td>
                  <td className="px-3 py-2 text-right">{closed ? row.tecLinesWrittenOff : '—'}</td>
                  <td className="px-3 py-2">
                    {closed && row.writeOffInvoiceId ? (
                      <a
                        href={u2000BuildAppRouteHref(`/U3125/${row.writeOffInvoiceId}`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.writeOffInvoiceNumber ?? row.writeOffInvoiceId}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {closed && row.standardInvoicesClosed?.length > 0 ? (
                      <ul className="list-inside list-disc text-xs">
                        {row.standardInvoicesClosed.map((inv) => (
                          <li key={inv.invoiceId}>
                            <a
                              href={u2000BuildAppRouteHref(`/U3125/${inv.invoiceId}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {inv.invoiceNumber}
                            </a>
                            {' '}
                            ({inv.previousStatus})
                          </li>
                        ))}
                      </ul>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="max-w-md px-3 py-2 text-gray-600">
                    {row.status === 'error' ? row.message ?? row.errorCode ?? '—' : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="form-card-footer shrink-0 border-t border-gray-200 py-3">
        <U2000CommonTableFooter
          nbItem={results.length}
          label={t('lblProject', { count: results.length, ns: NS })}
        />
      </footer>

      <div className="flex flex-wrap justify-end gap-2">
        <U2000ActionBtn
          variant="secondary"
          label={t('wizardNewSession', { ns: NS })}
          handleClick={onNewSession}
        />
        <U2000ActionBtn label={t('wizardBackToProjectList', { ns: NS })} handleClick={onBackToList} />
      </div>
    </div>
  );
};

export default U3146CloseInactiveWizardCloseResults;
